import React, { useMemo } from "react";
import { DateTime } from "luxon";
import { BarChart } from "@mantine/charts";

import {
  convertDateRangeToTimePeriods,
  type TimeGranularity,
} from "@utils/chart-data-conversions/timelines";

import {
  formatTimePeriodForChartLabel,
  extractPlayerActivityForTimePeriod,
} from "../helpers";

import type { PlayerActivity } from "@neuromotion-inc/mightier-ts-sdk";

interface PlayerActivityChartProps {
  playerActivityInTimeRange: PlayerActivity[];
  dateRangeStart: DateTime;
  dateRangeEnd: DateTime;
  granularity: TimeGranularity;
}

interface BarChartColumn {
  xAxisLabel: string;
  playtimeMinutes: number;
}

const KEY_NAME: keyof BarChartColumn = "playtimeMinutes";

export const PlayerActivityChart: React.FC<PlayerActivityChartProps> = (
  props,
) => {
  const {
    dateRangeStart,
    dateRangeEnd,
    granularity,
    playerActivityInTimeRange: data,
  } = props;

  const periods = useMemo(() => {
    return convertDateRangeToTimePeriods(
      granularity,
      dateRangeStart,
      dateRangeEnd,
    );
  }, [granularity, dateRangeStart, dateRangeEnd]);

  const chartData = useMemo<BarChartColumn[]>(() => {
    return periods.map((pd) => {
      const activityDuringPeriod = extractPlayerActivityForTimePeriod(
        data,
        pd.start,
        pd.end,
      );
      return {
        xAxisLabel: formatTimePeriodForChartLabel(pd),
        playtimeMinutes:
          activityDuringPeriod.reduce((acc, curr) => {
            return acc + curr.seconds_played;
          }, 0) / 60,
      } satisfies BarChartColumn;
    });
  }, [data, periods]);

  return (
    <BarChart
      h={300}
      data={chartData}
      dataKey="xAxisLabel"
      mt="md"
      series={[{ name: KEY_NAME, color: "violet.6" }]}
    />
  );
};
